import { Chess, type Square } from 'chess.js';
import type { Color, PunishmentFacts } from '../../types';

/** Valeurs de pièces en centipions, utilisées pour juger si une capture ou
 * une fourchette porte sur une pièce "significative" (>= mineure). Partagé
 * avec `themes.ts`. */
export const PIECE_VALUES: Record<string, number> = { p: 100, n: 300, b: 300, r: 500, q: 900, k: 0 };

/**
 * Calcule les faits mécaniques de la réponse adverse qui punit un coup joué,
 * à partir de la position qui suit ce coup (`fenAfterPlayed`) et de la
 * meilleure ligne adverse depuis cette position (`opponentBestPvUci`, telle
 * que renvoyée par le moteur). Purement vérifié — rien n'est déduit d'une
 * intention supposée du joueur ou de l'adversaire.
 *
 * Retourne `undefined` si aucune réponse exploitable n'est disponible (PV
 * vide, ou premier coup du PV illégal dans cette position — ne devrait pas
 * arriver si le PV vient bien de cette position, mais on ne throw jamais).
 */
export function computeReplyFacts(
  fenAfterPlayed: string,
  opponentBestPvUci: string[] | undefined,
  playerColor: Color,
): PunishmentFacts | undefined {
  if (!opponentBestPvUci || opponentBestPvUci.length === 0) return undefined;

  let chess: Chess;
  try {
    chess = new Chess(fenAfterPlayed);
  } catch {
    return undefined;
  }

  const opponentColor: Color = playerColor === 'w' ? 'b' : 'w';

  // Rejoue toute la ligne indicative sur une seule position, pour détecter
  // un éventuel mat forcé au passage (pas seulement au premier coup).
  const lineSanFull: string[] = [];
  const lineUciFull: string[] = [];
  let mateInMoves: number | undefined;
  let firstMove: ReturnType<Chess['move']> | null = null;

  for (const uci of opponentBestPvUci) {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    let move;
    try {
      move = chess.move({ from: from as Square, to: to as Square, promotion });
    } catch {
      move = null;
    }
    if (!move) break;
    if (!firstMove) firstMove = move;
    lineSanFull.push(move.san);
    lineUciFull.push(uci);
    if (chess.isCheckmate()) {
      mateInMoves = Math.ceil(lineSanFull.length / 2);
      break;
    }
  }

  if (!firstMove) return undefined;

  const replyUci = opponentBestPvUci[0];
  const replyTo = replyUci.slice(2, 4);
  const replyPromotion = replyUci.length > 4 ? replyUci.slice(4, 5) : undefined;

  // givesCheck / captured / forkedSquares portent sur la position juste
  // après la réponse (le premier coup), pas sur la fin de la ligne indicative
  // ci-dessus — rejoué séparément pour ne dépendre que de ce seul coup.
  const afterReply = new Chess(fenAfterPlayed);
  afterReply.move({ from: replyUci.slice(0, 2) as Square, to: replyTo as Square, promotion: replyPromotion });
  const givesCheck = afterReply.inCheck();

  let captured: PunishmentFacts['captured'];
  if (firstMove.captured) {
    const attackers = afterReply.attackers(replyTo as Square, opponentColor).length;
    const defenders = afterReply.attackers(replyTo as Square, playerColor).length;
    captured = { piece: firstMove.captured, square: replyTo, attackers, defenders };
  }

  // Fourchette : depuis sa case d'arrivée, la pièce qui vient de jouer
  // attaque au moins deux pièces du joueur de valeur >= mineure.
  const forkedSquares = afterReply
    .board()
    .flat()
    .filter((sq): sq is NonNullable<typeof sq> => sq !== null)
    .filter((sq) => sq.color === playerColor && (PIECE_VALUES[sq.type] ?? 0) >= 300)
    .filter((sq) => afterReply.attackers(sq.square as Square, opponentColor).includes(replyTo as Square))
    .map((sq) => sq.square);

  return {
    fenAfterPlayed,
    replySan: firstMove.san,
    replyUci,
    lineSan: lineSanFull.slice(0, 4),
    lineUci: lineUciFull.slice(0, 4),
    captured,
    givesCheck,
    forkedSquares,
    mateInMoves,
  };
}
